import { ReactNode } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export type UsageAccent = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

// The percentage change of `current` against the previous period, shown as an up/down arrow and
// a percentage. For usage, more is the positive direction, so up is green and down is red. When
// the previous period was zero there is no baseline to divide by, so only the arrow shows.
export const UsageDelta = ({ current, previous }: { current?: number; previous?: number }) => {
  if (current === undefined || previous === undefined) return null;
  const diff = current - previous;
  if (diff === 0) return null;
  const up = diff > 0;
  const pct = previous > 0 ? Math.round((diff / previous) * 100) : null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.75, flexShrink: 0, color: up ? 'success.main' : 'error.main' }}>
      {up ? <ArrowUpwardIcon sx={{ fontSize: '0.8rem' }} /> : <ArrowDownwardIcon sx={{ fontSize: '0.8rem' }} />}
      <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>{pct !== null ? `${Math.abs(pct)}%` : 'new'}</Typography>
    </Box>
  );
};

// A KPI tile: the value and its label, a soft tinted icon bubble and a coloured left accent,
// so the row of stats reads with a bit of colour rather than as plain outlined boxes.
export const UsageStatCard = ({ label, value, icon, accent = 'primary', previous }: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  accent?: UsageAccent;
  previous?: number;
}) => {
  const isNumber = typeof value === 'number';
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: 2,
        borderLeft: `4px solid ${theme.palette[accent].main}`,
      })}
    >
      {icon && (
        <Box
          sx={(theme) => ({
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette[accent].dark || theme.palette[accent].main,
            bgcolor: alpha(theme.palette[accent].main, 0.14),
          })}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, flexWrap: 'wrap' }}>
          <Typography variant={isNumber ? 'h5' : 'body1'} sx={{ color: 'text.primary', lineHeight: 1.2 }}>{value}</Typography>
          {isNumber && <UsageDelta current={value as number} previous={previous} />}
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      </Box>
    </Paper>
  );
};

// A framed section for one breakdown: an uppercase eyebrow title, an optional action on the
// right, then the content. Gives the middle of the usage pages a consistent card rhythm.
export const UsageBreakdownCard = ({ title, action, children }: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, minHeight: 32 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

// A label/count list where each row carries a faint bar sized to its share of the largest
// value, so relative magnitudes are scannable without reading every number.
export const UsageBreakdownList = ({ items, emptyText = 'Nothing recorded in this window.' }: {
  items: { label: string; count: number; previous?: number }[];
  emptyText?: string;
}) => {
  if (items.length === 0) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>{emptyText}</Typography>;
  }
  const max = items.reduce((m, i) => Math.max(m, i.count), 0) || 1;
  return (
    <Stack spacing={0.75}>
      {items.map((item, idx) => (
        <Box key={`${item.label}-${idx}`}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.25 }}>
            <Typography variant="body2" sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>{item.count}</Typography>
              <UsageDelta current={item.count} previous={item.previous} />
            </Box>
          </Box>
          <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'lightGrey.light', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.max((item.count / max) * 100, 2)}%`, bgcolor: 'primary.light', borderRadius: 3 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

// The optional organisation capabilities we surface, in display order. Keys match the
// settings object the backend returns (adoption counts on the platform view, on/off per org).
export const USAGE_SETTINGS: { key: string; label: string }[] = [
  { key: 'subsidiaries', label: 'Subsidiaries' },
  { key: 'projects', label: 'Projects' },
  { key: 'recs', label: 'Renewable Energy Certificates' },
  { key: 'flag', label: 'FLAG Emissions' },
  { key: 'pcf', label: 'Product Carbon Footprint' },
  { key: 'tndLosses', label: 'Transmission & Distribution Losses' },
  { key: 'inflation', label: 'Inflation Adjustment (spend-based)' },
];

// The entity types whose "added in this window" counts we surface, in display order.
export const USAGE_ENTITIES: { key: string; label: string }[] = [
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'projects', label: 'Projects' },
  { key: 'products', label: 'Products' },
];

export interface UsageImportRow { importType: string; viaBill: boolean; billType: string | null; uploaded: number; committed: number; prevCommitted?: number; }

// Labels an imports-by-path row. Bills reuse a generic import type under the hood, so show the
// bill kind ("Energy bill" / "Fuel bill") rather than the raw type when it came via a bill.
export const importPathLabel = (item: { importType: string; viaBill: boolean; billType: string | null }): string => {
  if (item.viaBill) {
    const kind = item.billType ? item.billType.charAt(0).toUpperCase() + item.billType.slice(1) : 'Document';
    return `${kind} bill`;
  }
  return item.importType;
};

// The imports-by-path list (uploaded vs committed per path), used on both usage views.
export const UsageImportsList = ({ items }: { items: UsageImportRow[] }) => {
  if (items.length === 0) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nothing recorded in this window.</Typography>;
  }
  return (
    <Stack spacing={1}>
      {items.map((item, idx) => (
        <Box key={`${item.importType}-${item.viaBill ? (item.billType || 'bill') : 'file'}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>{importPathLabel(item)}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.uploaded} uploaded, {item.committed} committed</Typography>
            <UsageDelta current={item.committed} previous={item.prevCommitted} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

// One org's capability settings as on/off chips (the per-org equivalent of platform adoption).
export const UsageSettingsStatus = ({ settings }: { settings: { [key: string]: boolean } }) => (
  <Stack spacing={1}>
    {USAGE_SETTINGS.map(s => (
      <Box key={s.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{s.label}</Typography>
        <Chip size="small" label={settings[s.key] ? 'On' : 'Off'} color={settings[s.key] ? 'success' : 'default'} variant={settings[s.key] ? 'filled' : 'outlined'} />
      </Box>
    ))}
  </Stack>
);

// A percentage bar used for rates (e.g. suggestion acceptance), where the fill maps directly
// to the percent rather than to a share of the largest value.
export const UsageRateRow = ({ label, detail, percent }: {
  label: string;
  detail: string;
  percent: number;
}) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>{detail}</Typography>
    </Box>
    <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'lightGrey.light', overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${Math.min(Math.max(percent, 0), 100)}%`, bgcolor: 'primary.main', borderRadius: 3 }} />
    </Box>
  </Box>
);
